import { Component, EventEmitter, HostListener, inject, input, Input, OnInit, output, Output, signal, ViewChild } from '@angular/core';
import { VideoResponseDto, VideoCreatorResponseDto, VideoStatusEnum, VideoFeedbackResponseDto, ClientReviewResultDto, ClientReviewStateDto } from '../../../../common/general-types/videos.dto';
import { SHOUT_INTERNAL_ACCESS_ROLES } from '../../../../common/general-types/users.dto';
import { CreatorResponseDto } from '../../../../common/general-types/creators.dto';
import { BriefApplicationResponseDto, SocialChannelEnum, VideoFormatEnum } from '../../../../common/general-types/briefs.dto';
import {
  briefIsUgcOnly,
  resultsCallToAction as callToActionForResults,
  resultsCrossPlatforms as crossPlatformsForResults,
  resultsHashtags as hashtagsForResults,
  resultsPrimaryChannel as primaryChannelForResults,
} from './video-feedback.results';
import { SubmittedPostDto } from '../../../../common/general-types/submitted-posts.dto';
import { LdgImgComponent } from '../../../../atoms/ldg-img/ldg-img.component';
import { ImageService } from '../../../../services/image/image.service';
import { FormatService } from '../../../../common/format/format.service';
import { ViewSubManageService } from '../../services/view-sub-manage/view-sub-manage.service';
import { TooltipDirective } from '../../../../common/directives/tooltip/tooltip.directive';
import { S3uploaderService } from '../../../../services/s3uploader/s3uploader.service';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { VideoAiModerateService } from '../../../../services/video-ai-moderate/video-ai-moderate.service';
import { firstValueFrom, take } from 'rxjs';
import { VideoService } from '../../../../services/video/video.service';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { AdLicenseDto, AdLicenseQuoteDto, AdLicenseSlotState } from '../../../../common/general-types/ad-license.dto';
import { resolveAdLicenseSlot } from '../../../../services/ad-license/ad-license-state';
import { videosRequired } from '../../../../common/brief-pieces/piece-rules';

type VideoFeedbackTab =
  | 'feedback'
  | 'details'
  | 'analysis'
  | 'client-review'
  | 'results'
  | 'tto'
  | 'comments';
import { LoadingInlineComponent } from '../../../../atoms/loading-inline/loading-inline.component';
import { FirebaseService } from '../../../../common/firebase/firebase.service';
import { ContentReviewResponse, ContentReviewResponseV2 } from '../../../../common/general-types/ai-video.dto';
import { VideoWithControlsComponent } from '../../../../atoms/video-with-controls/video-with-controls.component';
import { environment } from '../../../../../environments/environment';
import { ChoosePlanService } from '../../../choose-plan/service/choose-plan/choose-plan.service';
import { IndividualPostResultsComponent } from '../../../../atoms/individual-post-results/individual-post-results.component';
import { IndividualPostResultsTtoComponent } from '../../../../atoms/individual-post-results-tto/individual-post-results-tto';
import { IndividualPostCommentsComponent } from '../../../../atoms/individual-post-comments/individual-post-comments.component';
import { IndividualPostAiReviewComponent } from '../../../../atoms/individual-post-ai-review/individual-post-ai-review.component';
import { TtoCampaignVideosDto } from '../../../../common/general-types/tiktok-one-reporting.dto';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { BriefApplicationService } from '@/app/services/brief-application/brief-application.service';
import { BriefService } from '@/app/services/brief/brief.service';
import { ViewportOverlayDirective } from '@/app/common/directives/viewport-overlay/viewport-overlay.directive';
import { WysiswygComponent } from '@/app/atoms/wysiswyg/wysiswyg.component';
import { CopyId } from '@/app/atoms/copy-id/copy-id';
import { SubmittedPostFields } from '@/app/atoms/individual-post-results/atoms/submitted-post-fields/submitted-post-fields';
import { SubmittedPostsService } from '@/app/services/submitted-posts/submitted-posts.service';
import { HasRoleDirectiveDirective } from '@/app/common/directives/has-role/has-role-directive.directive';

@Component({
  selector: 'vs-video-feedback',
  standalone: true,
  imports: [LdgImgComponent, TooltipDirective, FormsModule, AsyncPipe, DatePipe, CurrencyPipe,
    LoadingInlineComponent, VideoWithControlsComponent, IndividualPostResultsComponent,
    IndividualPostResultsTtoComponent, IndividualPostCommentsComponent,
    IndividualPostAiReviewComponent, WysiswygComponent, CopyId,
    SubmittedPostFields, HasRoleDirectiveDirective],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './video-feedback.component.html',
  styleUrl: './video-feedback.component.css'
})
export class VideoFeedbackComponent implements OnInit {

  environment = environment;

  @HostListener('document:keydown.escape', ['$event' as any])
  handleEscapeKey(event: KeyboardEvent) {
    if (this.keyboardSuspended()) return;
    this.closeVideoFeedback();
  }

  @HostListener('document:keydown.arrowleft', ['$event'])
  handleArrowLeft(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (this.keyboardSuspended() || this.isTypingTarget(keyboardEvent.target) || !this.canGoToPreviousVideo()) {
      return;
    }
    keyboardEvent.preventDefault();
    this.goToPreviousVideo();
  }

  @HostListener('document:keydown.arrowright', ['$event'])
  handleArrowRight(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (this.keyboardSuspended() || this.isTypingTarget(keyboardEvent.target) || !this.canGoToNextVideo()) {
      return;
    }
    keyboardEvent.preventDefault();
    this.goToNextVideo();
  }

  @Input() video: any | VideoCreatorResponseDto | VideoResponseDto | undefined;
  @Input() videoId: string | undefined;
  @Input() videoFormat!: VideoFormatEnum;
  @Input() creator!: CreatorResponseDto;
  @Input() isUgcOnly: boolean = false;
  /** Optional ordered list — when length > 1, side arrows navigate between drafts. */
  @Input() videos: VideoResponseDto[] = [];

  @Input() briefApplication: BriefApplicationResponseDto | undefined;
  @Input() briefId: string | undefined;

  @Input() defaultMessageText: string | undefined = undefined;

  @Input() ttoResults: TtoCampaignVideosDto | undefined = undefined;

  @Output() close = new EventEmitter<void>();
  @Output() approveVideo = new EventEmitter<VideoResponseDto>();
  @Output() videoChange = new EventEmitter<VideoResponseDto>();

  /** Ad License: the quote for this application; null when licences can't be bought. */
  readonly licenseQuote = input<AdLicenseQuoteDto | null>(null);
  /** Ad License: the latest licence per video id. */
  readonly licensesByVideoId = input<Record<string, AdLicenseDto>>({});
  readonly buyLicense = output<VideoResponseDto>();
  /** True while a modal (e.g. Buy Ad License) sits on top, so Esc/arrows don't act on the player. */
  readonly keyboardSuspended = input<boolean>(false);

  @ViewChild('videoFeedbackWysiwyg') private videoFeedbackWysiwyg?: WysiswygComponent;

  selectedTab = signal<VideoFeedbackTab>('feedback');

  videoFeedback = signal<VideoFeedbackResponseDto[] | undefined>(undefined);
  unreadFeedback = signal<VideoFeedbackResponseDto[] | undefined>(undefined);
  reRunNoodleLoading = signal<boolean>(false);

  /** Feeddback Variables */
  feedbackComment: string | undefined;
  feedbackCommentHtml = signal<string | undefined>(undefined);
  sendingFeedback = signal<boolean>(false);
  highlightItem = signal<string | undefined>(undefined);
  /** Feeddback Variables END */

  /** Review Variables */
  reviewHistory = signal<any | ContentReviewResponse | ContentReviewResponseV2 | undefined>(undefined);
  displayReviewItems = signal<any[]>([]);
  /** Review Variables END */

  approvalLoading = signal<boolean>(false);

  sendingToClientReview = signal<boolean>(false);
  pickingPostSlot = signal<boolean>(false);
  clientReviewState = signal<ClientReviewStateDto | undefined>(undefined);
  clientReviewLinkCopied = signal(false);
  private clientReviewCopyTimeout: ReturnType<typeof setTimeout> | null = null;

  submittedPosts = signal<SubmittedPostDto[] | undefined>(undefined);
  detailsLoadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');


  /** Services */
  public imageService = inject(ImageService);
  public formatService = inject(FormatService);
  public viewSubManageService = inject(ViewSubManageService);
  public uploadService = inject(S3uploaderService);
  public currentUserService = inject(CurrentUserService);
  public videoAiModerateService = inject(VideoAiModerateService);
  private videoService = inject(VideoService);
  private firebaseService = inject(FirebaseService);
  private choosePlanService = inject(ChoosePlanService);
  private toastNotificationService = inject(ToastNotificationService);
  private briefApplicationService = inject(BriefApplicationService);
  private briefService = inject(BriefService);
  private submittedPostsService = inject(SubmittedPostsService);
  /** Services END */

  protected readonly VideoStatusEnum = VideoStatusEnum;

  /** Licence for the video currently shown (the player can page between drafts). */
  currentLicense(): AdLicenseDto | null {
    return this.video?.id ? this.licensesByVideoId()[this.video.id] ?? null : null;
  }

  currentLicenseSlot(): AdLicenseSlotState {
    return resolveAdLicenseSlot(this.video?.status, this.licenseQuote(), this.currentLicense());
  }

  usageRightsLabel(): string {
    switch (this.currentLicenseSlot()) {
      case 'licensed':
        return 'Ad License';
      case 'pending':
        return 'License pending';
      default:
        return 'UGC only';
    }
  }

  licenseCurrency(): string {
    return (this.currentLicense()?.currencyCode ?? this.licenseQuote()?.currencyCode ?? 'gbp').toUpperCase();
  }

  quotePrice(): number {
    return (this.licenseQuote()?.brandPrice ?? 0) / 100;
  }

  pendingLicensePrice(): number {
    return (this.currentLicense()?.brandPrice ?? 0) / 100;
  }

  onBuyLicense(): void {
    if (this.video) this.buyLicense.emit(this.video);
  }

  ngOnInit(): void {
    if (this.defaultMessageText) {
      this.feedbackComment = this.defaultMessageText;
    }

    if (!this.briefApplication && this.briefId) {
      this.briefApplicationService.findMyApplicationWithBrief(this.briefId, this.creator.blazeFirebaseUid).pipe(take(1)).subscribe((briefApplication: any) => {
        this.briefApplication = briefApplication;
        this.checkSeenVideo();
        this.loadVideoFeedback();
        this.loadClientReviewState();
        if (this.selectedTab() === 'details') {
          this.loadSubmittedPostDetails();
        }
      })
    } else {
      this.checkSeenVideo();
      this.loadVideoFeedback();
      this.loadClientReviewState();
    }


  }

  async checkSeenVideo() {
    console.log('🐬🐬🐬 checkSeenVideo()');
    if (this.video) {
      if (this.video.status == VideoStatusEnum.New) {
        try {
          const updateSeen = await firstValueFrom(this.videoService.updateVideoStatus(this.video.id, VideoStatusEnum.Seen))
        } catch (updateSeenVidErr) {
          console.warn('updateSeenVidErr: ', updateSeenVidErr);
        }
      }
    }
  }

  loadVideoFeedback() {
    if (this.video) {
      this.videoService.getVideoFeedback(this.video.id).pipe(take(1)).subscribe((videoFeedback: VideoFeedbackResponseDto[]) => {

        console.log('👀👀👀 videoFeedback: ', videoFeedback);
        this.videoFeedback.set(videoFeedback);
        this.unreadFeedback.set(videoFeedback.filter((feedback: VideoFeedbackResponseDto) => !(feedback.seenBy ?? []).includes(this.firebaseService.userId || '')));

        if (this.unreadFeedback() && this.unreadFeedback()!.length > 0) {
          this.startMarkingFeedbackAsRead();
        }
      })
    } else {
      this.videoService.getVideoById(this.videoId!).pipe(take(1)).subscribe((video: VideoResponseDto) => {
        this.video = video;
        // Callers that open this drawer by id have no video at ngOnInit, so the earlier
        // client review lookup bailed out. Now that we have the draft, it can run.
        this.loadClientReviewState();
        this.loadVideoFeedback();
        if (this.selectedTab() === 'details') {
          this.loadSubmittedPostDetails();
        }
      })
    }
  }

  /**
   * `ViewSubManageService` is only populated by the full view-submission page, so when this modal
   * is opened from other surfaces (activity feed, reporting) the gate must fall back to the data
   * passed in via inputs, otherwise approval is silently unavailable.
   */
  canApproveVideo(video: VideoResponseDto): boolean {
    if (video.status !== VideoStatusEnum.New && video.status !== VideoStatusEnum.Seen) {
      return false;
    }
    if (!this.resolveBriefId()) {
      return false;
    }
    const required = this.requiredVideoCount();
    return required <= 0 || this.approvedVideoCount() < required;
  }

  /**
   * Why approval is unavailable for a video that is still awaiting a decision, so the UI can show
   * a disabled Approve button with an explanation rather than omitting the control entirely.
   * Returns null once the video has been decided — those already show their own status badge.
   */
  approvalBlockedReason(video: VideoResponseDto): string | null {
    if (this.canApproveVideo(video)) {
      return null;
    }
    if (video.status !== VideoStatusEnum.New && video.status !== VideoStatusEnum.Seen) {
      return null;
    }
    if (!this.resolveBriefId()) {
      return 'This content is not linked to a brief, so it cannot be approved here.';
    }

    const required = this.requiredVideoCount();
    const approved = this.approvedVideoCount();
    return `This brief only needs ${required} ${required === 1 ? 'video' : 'videos'} from this creator, and ${approved === 1 ? '1 has' : `${approved} have`} already been approved.`;
  }

  async explainApprovalBlocked(video: VideoResponseDto): Promise<void> {
    const reason = this.approvalBlockedReason(video);
    if (!reason) {
      return;
    }

    await this.toastNotificationService.alert({
      title: 'Approval not available',
      message: reason,
      type: 'info',
      icon: 'info'
    });
  }

  private requiredVideoCount(): number {
    const applicationBrief = this.briefApplication?.brief;
    const hostBrief = this.briefService.focussedBriefSignal();
    const matchingHost =
      hostBrief?.id && hostBrief.id === this.resolveBriefId() ? hostBrief : undefined;
    const brief = applicationBrief ?? matchingHost;

    if (brief || this.briefApplication?.hireRecord) {
      return videosRequired({
        pieces: brief?.pieces,
        videosPerCreator: brief?.videosPerCreator,
        owed: this.briefApplication?.hireRecord?.owed,
      });
    }

    return this.viewSubManageService.requiredVideos();
  }

  /** Client-review slots still follow the legacy video count stored on the brief. */
  private contractedVideoCount(): number {
    const applicationBrief = this.briefApplication?.brief;
    if (applicationBrief?.videosPerCreator != null) {
      return applicationBrief.videosPerCreator;
    }
    const hostBrief = this.briefService.focussedBriefSignal();
    if (hostBrief?.id && hostBrief.id === this.resolveBriefId()) {
      return hostBrief.videosPerCreator ?? 0;
    }
    return this.viewSubManageService.requiredVideos();
  }

  private approvedVideoCount(): number {
    if (this.videos.length > 0) {
      return this.videos.filter(item => item.status === VideoStatusEnum.Accepted).length;
    }
    return this.viewSubManageService.approvedVideos()?.length ?? 0;
  }

  private resolveBriefId(): string | undefined {
    return this.briefApplication?.briefId ?? this.briefId;
  }

  async triggerVideoApproval(video: VideoResponseDto) {
    console.log('VFC - 🤢🤢🤢 Approve Video()', video);
    console.log('VFC - 🤢🤢🤢 Approved Videos', this.approvedVideoCount());
    console.log('VFC - 🤢🤢🤢 videosPerCreator', this.requiredVideoCount());

    const briefId = this.resolveBriefId();
    if (!briefId) {
      return;
    }

    this.approvalLoading.set(true);

    void this.videoService.approveVideoModern(video.id, briefId).subscribe({
      next: (response: any) => {
        console.log('VFC - 🤢🤢🤢 Approve Video Response: ', response);
        this.video.status = VideoStatusEnum.Accepted;
        this.approvalLoading.set(false);
        this.approveVideo.emit(video);
      },
      error: error => {
        console.error('VFC - Error approving video:', error);
        this.approvalLoading.set(false);
      }
    });
  }

  cancelApprovalProcess() {

  }

  /** How many posts this brief contracted — drives Post 1 / 2 / 3 on send. */
  expectedPosts(): number {
    return Math.max(1, this.contractedVideoCount() || 1);
  }

  postSlots(): number[] {
    return Array.from({ length: this.expectedPosts() }, (_, i) => i + 1);
  }

  onSendToClientReviewClick(): void {
    if (this.expectedPosts() <= 1) {
      this.sendToClientReview(1);
      return;
    }
    this.pickingPostSlot.set(true);
  }

  cancelPostPick(): void {
    this.pickingPostSlot.set(false);
  }

  /**
   * Send the draft currently open in this drawer to Mission Control client review.
   * Acts on this exact video and brief — the reviewer never re-picks the creator or draft.
   * Leaves video.status untouched; a failure here must not alter the draft's SHOUT state.
   */
  sendToClientReview(postSlot = 1) {
    const briefId = this.resolveBriefId();

    if (!this.video?.id || !briefId || this.sendingToClientReview()) {
      return;
    }

    this.pickingPostSlot.set(false);
    this.sendingToClientReview.set(true);

    this.videoService.sendToClientReview(this.video.id, briefId, postSlot).pipe(take(1)).subscribe({
      next: async (result: ClientReviewResultDto) => {
        this.clientReviewState.set({ inClientReview: true, ...result });
        this.selectedTab.set('client-review');
        this.sendingToClientReview.set(false);
        await this.toastNotificationService.alert({
          title: result.alreadySent ? 'Already in client review' : 'Sent to client review',
          message: result.alreadySent
            ? 'This draft is already on the client review list for this brief.'
            : 'The client can now review this draft.',
          type: 'success',
          icon: 'success'
        });
      },
      error: async error => {
        console.error('VFC - Error sending video to client review:', error);
        this.sendingToClientReview.set(false);
        await this.toastNotificationService.alert({
          title: 'Could not send to client review',
          message: error?.error?.message || 'Please try again.',
          type: 'error',
          icon: 'error'
        });
      }
    });
  }

  /**
   * Read the draft's live client-review state. Without this the badge would only reflect
   * whether the button was pressed in this browser session, and would revert on reload.
   */
  private loadClientReviewState() {
    const briefId = this.resolveBriefId();
    if (!this.video?.id || !briefId) {
      return;
    }

    this.videoService.getClientReviewState(this.video.id, briefId).pipe(take(1)).subscribe({
      next: (state: ClientReviewStateDto) => {
        this.clientReviewState.set(state?.inClientReview ? state : undefined);
      },
      // A badge is not worth surfacing an error for; leaving it unset shows the send button.
      error: () => this.clientReviewState.set(undefined),
    });
  }

  /** True once this draft is known to be on a client review list. */
  isInClientReview(): boolean {
    return !!this.clientReviewState()?.inClientReview;
  }

  isClientReviewApproved(): boolean {
    return this.clientReviewState()?.status === 'approved';
  }

  isClientReviewDeclined(): boolean {
    return this.clientReviewState()?.status === 'declined';
  }

  /** Label reflecting what the client has actually done, not just that we sent it. */
  clientReviewLabel(): string {
    switch (this.clientReviewState()?.status) {
      case 'approved':
        return 'Client approved';
      case 'declined':
        return 'Client declined';
      case 'commented':
        return 'Client left feedback';
      default:
        return 'In Client Review';
    }
  }

  /** Tailwind colour set matching the review outcome. */
  clientReviewTone(): string {
    switch (this.clientReviewState()?.status) {
      case 'approved':
        return 'bg-green-400/10 text-green-400 ring-green-400/20';
      case 'declined':
        return 'bg-red-400/10 text-red-400 ring-red-400/20';
      case 'commented':
        return 'bg-sky-400/10 text-sky-400 ring-sky-400/20';
      default:
        return 'bg-amber-400/10 text-amber-400 ring-amber-400/20';
    }
  }

  clientReviewDotTone(): string {
    switch (this.clientReviewState()?.status) {
      case 'approved':
        return 'fill-green-400';
      case 'declined':
        return 'fill-red-400';
      case 'commented':
        return 'fill-sky-400';
      default:
        return 'fill-amber-400';
    }
  }

  openClientReview() {
    const url = this.clientReviewShareUrl();
    if (url) {
      window.open(url, '_blank', 'noopener');
    }
  }

  /** Public Mission Control draft-review URL the client is sent. */
  clientReviewShareUrl(): string | null {
    return this.clientReviewState()?.shareUrl?.trim() || null;
  }

  clientReviewShareHost(): string | null {
    return this.parseClientReviewShareUrl()?.host || null;
  }

  clientReviewSharePath(): string | null {
    const parsed = this.parseClientReviewShareUrl();
    if (!parsed) {
      return this.clientReviewShareUrl();
    }
    return `${parsed.pathname}${parsed.search}`;
  }

  async copyClientReviewLink(): Promise<void> {
    const url = this.clientReviewShareUrl();
    if (!url) {
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      this.clientReviewLinkCopied.set(true);
      if (this.clientReviewCopyTimeout) {
        clearTimeout(this.clientReviewCopyTimeout);
      }
      this.clientReviewCopyTimeout = setTimeout(() => {
        this.clientReviewLinkCopied.set(false);
        this.clientReviewCopyTimeout = null;
      }, 2000);
      await this.toastNotificationService.alert({
        title: 'Copied',
        message: 'Client review link copied to clipboard.',
        type: 'success',
        icon: 'success',
      });
    } catch {
      await this.toastNotificationService.alert({
        title: 'Copy failed',
        message: 'Could not copy the client review link.',
        type: 'error',
        icon: 'error',
      });
    }
  }

  clientReviewFeedback(): string | null {
    const feedback = this.clientReviewState()?.feedback?.trim();
    return feedback || null;
  }

  hasClientFeedback(): boolean {
    return !!this.clientReviewFeedback();
  }

  private parseClientReviewShareUrl(): URL | null {
    const url = this.clientReviewShareUrl();
    if (!url) {
      return null;
    }
    try {
      return new URL(url);
    } catch {
      return null;
    }
  }

  downloadContent() {
    if (this.video) {
      if (this.video.fullUrl.includes('firebasestorage.googleapis.com')) {
        this.uploadService.progressFirebaseDownload(this.video.fullUrl);
      } else {
        this.uploadService.secureVideoDownload(this.video.fullUrl, this.video.videoName);
      }
    }
  }

  haveIAlreadySeenThisFeedback(feedback: VideoFeedbackResponseDto) {
    if (this.firebaseService.userId) {
      if (feedback.seenBy && feedback.seenBy.length > 0 && feedback.seenBy.includes(this.firebaseService.userId)) {
        return true;
      }
    }
    return false;
  }

  async startMarkingFeedbackAsRead() {
    setTimeout(async () => {
      try {
        const response = await firstValueFrom(this.videoService.readVideoFeedback(this.unreadFeedback()!.map((feedback: VideoFeedbackResponseDto) => feedback.id)));
        console.log('👀👀👀 response: ', response);

        for (let updated of response) {
          let original = this.videoFeedback()?.find((feedback: VideoFeedbackResponseDto) => feedback.id == updated.id);
          if (original) {
            original.seenBy = updated.seenBy;
          }
          this.unreadFeedback.set(this.unreadFeedback()!.filter((feedback: VideoFeedbackResponseDto) => feedback.id != updated.id));
          this.highlightItem.set(undefined);
        }

      } catch (error) {
        console.error('Error marking feedback as read: ', error);
      }
    }, 5000);
  }


  addVideoFeedback() {
    if (((this.feedbackComment && this.feedbackComment.length > 0) || (this.feedbackCommentHtml() && this.feedbackCommentHtml()!.length > 0)) && this.video && this.firebaseService.userId) {



      this.sendingFeedback.set(true);
      this.videoService.addVideoFeedback(this.video.id, this.feedbackComment || '', this.feedbackCommentHtml() || '', this.firebaseService.userId).pipe(take(1)).subscribe((createdFeedback: VideoFeedbackResponseDto) => {
        this.videoFeedback.set([createdFeedback, ...this.videoFeedback()!]);
        this.sendingFeedback.set(false);
        this.feedbackComment = undefined;
        this.feedbackCommentHtml.set(undefined);
        this.videoFeedbackWysiwyg?.applyExternalHtml('');

        this.highlightItem.set(createdFeedback.id);


        setTimeout(() => {
          const feedbackElement = document.getElementById(createdFeedback.id);
          if (feedbackElement) {
            feedbackElement.scrollIntoView({ behavior: 'smooth' });
          }
        }, 150);

        setTimeout(() => {
          this.highlightItem.set(undefined);
        }, 3000);
      })
    }
  }

  setSelectedTab(tab: VideoFeedbackTab) {
    if (tab === 'details' && !this.canViewPostDetails()) {
      return;
    }
    this.selectedTab.set(tab);
    if (!this.reviewHistory() && tab == 'analysis') {
      this.getVideoReview();
    }
    if (tab === 'details') {
      this.loadSubmittedPostDetails();
    }
    if (tab === 'client-review') {
      this.loadClientReviewState();
    }
  }

  isSocialMediaBrief(): boolean {
    if (!briefIsUgcOnly(this.briefApplication?.brief)) {
      return true;
    }
    return !this.isUgcOnly;
  }

  /** Results is the live-post check. A piece-list brief with a social placement is not UGC only. */
  showsResultsTab(): boolean {
    return this.isSocialMediaBrief();
  }

  resultsPrimaryChannel(): number | undefined {
    return primaryChannelForResults(this.briefApplication?.brief, this.resultsPieceScope());
  }

  resultsCrossPlatforms(): (string | number)[] {
    return crossPlatformsForResults(this.briefApplication?.brief, this.resultsPieceScope());
  }

  private resultsPieceScope() {
    return {
      pieceLabel: this.video?.pieceLabel,
      owed: this.briefApplication?.hireRecord?.owed,
    };
  }

  resultsHashtags(): string | undefined {
    return hashtagsForResults(this.briefApplication?.brief, this.video?.pieceLabel);
  }

  resultsCallToAction(): string | undefined {
    return callToActionForResults(this.briefApplication?.brief, this.video?.pieceLabel);
  }

  /** SHOUT internal admins only — CreatorAdmin (6) and GodMode (2). */
  canViewPostDetails(): boolean {
    return this.isSocialMediaBrief() && this.currentUserService.hasAnyRole(SHOUT_INTERNAL_ACCESS_ROLES);
  }

  getSocialChannelLabel(channel: number): string {
    return SocialChannelEnum[channel] ?? 'Social post';
  }

  loadSubmittedPostDetails() {
    if (!this.canViewPostDetails()) {
      this.submittedPosts.set(undefined);
      this.detailsLoadingState.set('idle');
      return;
    }

    const applicationId = this.briefApplication?.id;
    const videoId = this.video?.id;
    if (!applicationId || !videoId) {
      this.submittedPosts.set([]);
      this.detailsLoadingState.set('loaded');
      return;
    }

    this.detailsLoadingState.set('loading');
    this.submittedPostsService.getSubmittedPostsForBriefApplication(applicationId).pipe(take(1)).subscribe({
      next: (posts: SubmittedPostDto[]) => {
        this.submittedPosts.set(posts.filter((post) => post.videoId === videoId));
        this.detailsLoadingState.set('loaded');
      },
      error: () => {
        this.detailsLoadingState.set('error');
      },
    });
  }

  onSubmittedPostDetailsSaved(updatedPost: SubmittedPostDto) {
    this.submittedPosts.update((posts) =>
      (posts ?? []).map((post) => (post.id === updatedPost.id ? { ...post, ...updatedPost } : post)),
    );
  }

  getVideoReview() {
    if (this.video && this.video.id) {
      this.videoAiModerateService.getReviewByVideoId(this.video.id).pipe(take(1)).subscribe((allReviews: any) => {
        console.log('👀👀👀 allReviews: ', allReviews);

        if (allReviews.length > 0) {
          this.reviewHistory.set(allReviews[0]);
        }

        console.log('👀👀👀 reviewHistory: ', this.reviewHistory);
        console.log('👀👀👀 reviewHistory.reviewResults: ', this.reviewHistory()?.reviewResults[0]);

        if (this.reviewHistory() && this.reviewHistory()!.reviewResults.length > 0) {
          this.displayReviewItems.set(this.reviewHistory()!.reviewResults);
        } else {
          console.log(' ------- Super ESLE')
          this.displayReviewItems.set([]);
        }
      })
    }
  }

  async reRunReview() {
    console.log('👀👀👀 reRunReview() -->');
    console.log('video: ', this.video);
    console.log('reviews: ', this.reviewHistory());

    let hoursDiff: number = 0;
    this.reRunNoodleLoading.set(true);

    if (this.video && this.video.reviewSentDate) {
      hoursDiff = this.getHoursDiff(this.video.reviewSentDate);
    } else if (this.video?.createdDate) {
      hoursDiff = this.getHoursDiff(this.video.createdDate);
    }

    console.log('hoursDiff: ', hoursDiff);

    // if (hoursDiff > 24 && !this.reviewHistory() && this.video?.id) {
    if (!this.reviewHistory() && this.video?.id) {
      console.log('👀👀👀 reRunReview() --> hoursDiff: ', hoursDiff);

      this.videoAiModerateService.retryContentReview(this.video.id).pipe(take(1)).subscribe(async (response: any) => {
        console.log('Re-Try Sent Successfully: ', response);
        this.reRunNoodleLoading.set(false);
        await this.toastNotificationService.alert({
          title: 'Analysis Submitted!',
          message: 'Please check back later for the results.',
          type: 'success',
          icon: 'success'
        })
      }, async error => {
        console.error('Error retrying review: ', error);
        this.reRunNoodleLoading.set(false);
        await this.toastNotificationService.alert({
          title: 'Error Re-Running Analysis!',
          message: 'Something went wrong. If the problem persists, please contact support.',
          type: 'error',
          icon: 'error'
        })
      });
    }
  }

  getHoursDiff(date: Date) {
    const createdDate = new Date(date);
    const now = new Date();
    const hoursDiff = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60);
    return hoursDiff;
  }

  getSelectedTabClass(tab: VideoFeedbackTab) {
    if (this.selectedTab() == tab) {
      return 'border-primary text-accent';
    }
    return 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700';
  }

  getSelectedTabIconClass(tab: VideoFeedbackTab) {
    if (this.selectedTab() == tab) {
      return 'text-accent';
    }
    return 'text-gray-400 group-hover:text-gray-500';
  }

  closeVideoFeedback() {
    this.close.emit();
  }

  creatorId(): string {
    return this.creator?.blazeFirebaseUid?.trim() || this.creator?.id || '';
  }

  canNavigateVideos(): boolean {
    return this.videos.length > 1 && !!this.video?.id;
  }

  currentVideoIndex(): number {
    if (!this.video?.id || this.videos.length === 0) {
      return -1;
    }
    return this.videos.findIndex((item) => item.id === this.video.id);
  }

  canGoToPreviousVideo(): boolean {
    return this.canNavigateVideos() && this.currentVideoIndex() > 0;
  }

  canGoToNextVideo(): boolean {
    const index = this.currentVideoIndex();
    return this.canNavigateVideos() && index >= 0 && index < this.videos.length - 1;
  }

  goToPreviousVideo(): void {
    const index = this.currentVideoIndex();
    if (index <= 0) {
      return;
    }
    this.selectVideoAt(index - 1);
  }

  goToNextVideo(): void {
    const index = this.currentVideoIndex();
    if (index < 0 || index >= this.videos.length - 1) {
      return;
    }
    this.selectVideoAt(index + 1);
  }

  private selectVideoAt(index: number): void {
    const nextVideo = this.videos[index];
    if (!nextVideo || nextVideo.id === this.video?.id) {
      return;
    }

    this.video = nextVideo;
    this.videoChange.emit(nextVideo);
    this.resetStateForVideoChange();
  }

  private resetStateForVideoChange(): void {
    this.videoFeedback.set(undefined);
    this.unreadFeedback.set(undefined);
    this.reviewHistory.set(undefined);
    this.displayReviewItems.set([]);
    this.feedbackComment = undefined;
    this.feedbackCommentHtml.set(undefined);
    this.highlightItem.set(undefined);
    this.approvalLoading.set(false);
    this.sendingFeedback.set(false);
    // Client review state belongs to one draft. Carrying it across made every draft in the
    // carousel look like it had been sent, because only ngOnInit ever loaded it.
    this.clientReviewState.set(undefined);
    this.sendingToClientReview.set(false);
    this.pickingPostSlot.set(false);
    this.videoFeedbackWysiwyg?.applyExternalHtml('');
    this.submittedPosts.set(undefined);
    this.detailsLoadingState.set('idle');

    this.checkSeenVideo();
    this.loadVideoFeedback();
    this.loadClientReviewState();
    if (this.selectedTab() === 'analysis') {
      this.getVideoReview();
    }
    if (this.selectedTab() === 'details') {
      this.loadSubmittedPostDetails();
    }
  }

  private isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false;
    }
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
  }

  getInputRows() {
    if (this.defaultMessageText) {
      return 8;
    }
    return 4;
  }

  openChoosePlan() {
    this.choosePlanService.launchSubscribeOrBoltOn(environment.stripe.shaas21products.bo_ai_moderation);
  }

  sendSuggestedFeedback(message: string) {
    this.defaultMessageText = message;
    this.feedbackComment = message;
    this.setSelectedTab('feedback');
  }

  feedbackIsHtml(html: string) {
    if (html.includes('<html>') || html.includes('<p>') || html.includes('<ul>') || html.includes('<ol>') || html.includes('<li>') || html.includes('<h1>') || html.includes('<h2>') || html.includes('<h3>') || html.includes('<h4>') || html.includes('<h5>') || html.includes('<h6>')) {
      return true;
    }
    return false;
  }

  onHtmlContentChange(event: string) {
    console.log('VFC - onHtmlContentChange() -> ', event);
    this.feedbackCommentHtml.set(event);
  }
}
